import { z } from 'zod'
import { clientStateSchema } from './clients.js'
import { linksSchema } from './links.js'
import {
  periodoSchema,
  provenanceSchema,
  sourceKindSchema,
  unitSchema,
} from './primitives.js'

/**
 * Módulo `/usage` — medición de uso y product growth.
 *
 * Mide el tercer estado de una cuenta, el que al panel le faltaba: Revenue dice
 * si se vende, Clientes dice si está instalado y esto dice si está corriendo.
 * La cadena queda **contratado → instalado → activo**, donde activo significa
 * con al menos una traza en el período.
 *
 * Tres reglas de este archivo, heredadas del spec base y no negociables:
 *
 * 1. **Nada se almacena derivado.** Quema, proyección de agotamiento, variación,
 *    costo por agente, costo por traza y participación por modelo se calculan en
 *    lectura con las funciones puras de `usage-derived.ts`.
 * 2. **Todo porcentaje y todo delta publica su base.** Por eso casi ningún campo
 *    porcentual viaja solo: viene con el `base` que permite recalcularlo a mano.
 * 3. **`null` es "sin dato", nunca cero.** Un agente sin medición no consume
 *    cero: no se sabe cuánto consume, y son cosas distintas en pantalla.
 *
 * **Permisos — la excepción documentada.** Este módulo lo ven los tres roles, a
 * diferencia de Revenue. El corte está en `UsagePayload`, que es una unión
 * discriminada por rol: el **costo de servir** viaja para todos porque habilita
 * decisiones de eficiencia; **lo que paga el cliente** y **el margen por cuenta**
 * solo para admin, porque son la facturación con otro nombre. El recorte se
 * aplica en el servidor (ver `MONEY_NULL_KEYS` / `USAGE_MONEY_NULL_KEYS` en el
 * middleware de RBAC), no en el front.
 */

// ---------------------------------------------------------------------------
// Entidades
// ---------------------------------------------------------------------------

/** De dónde sale una fila de consumo. Langfuse mide; la plataforma atribuye. */
export const usageSourceSchema = z.enum(['langfuse', 'plataforma'])
export type UsageSource = z.infer<typeof usageSourceSchema>

/**
 * Estado de un empleado digital.
 *
 * - `activo`: tuvo al menos una traza en el período. Es la definición del spec.
 * - `desplegado`: instalado y sin trazas. Es el corazón del módulo — figura como
 *   éxito en la brecha de activación y no está produciendo nada.
 * - `inactivo`: contratado y no desplegado, o apagado.
 */
export const agentEstadoSchema = z.enum(['activo', 'desplegado', 'inactivo'])
export type AgentEstado = z.infer<typeof agentEstadoSchema>

export const AGENT_ESTADO_LABELS: Record<AgentEstado, string> = {
  activo: 'Activo',
  desplegado: 'Desplegado',
  inactivo: 'Inactivo',
}

/** El texto que acompaña al color: nunca color solo (regla de accesibilidad). */
export const AGENT_ESTADO_DESCRIPCIONES: Record<AgentEstado, string> = {
  activo: 'Con al menos una traza en el período',
  desplegado: 'Instalado y sin ninguna traza en el período',
  inactivo: 'Contratado y todavía no desplegado',
}

/** Orden por riesgo: lo que está roto arriba, como en el Overview. */
export const AGENT_ESTADO_BY_RISK: readonly AgentEstado[] = [
  'desplegado',
  'inactivo',
  'activo',
]

/**
 * Ids del agente en los sistemas de origen.
 *
 * `langfuseSessionPrefix` es **la clave del cruce** Langfuse↔plataforma: sin él
 * no se puede saber qué sesiones son de este agente, y por eso su consumo se
 * reporta como "sin medición" en vez de como cero.
 */
export const agentExternalIdsSchema = z.object({
  plataformaAgentId: z.string().nullable(),
  langfuseSessionPrefix: z.string().nullable(),
})
export type AgentExternalIds = z.infer<typeof agentExternalIdsSchema>

export const EMPTY_AGENT_EXTERNAL_IDS: AgentExternalIds = {
  plataformaAgentId: null,
  langfuseSessionPrefix: null,
}

export function agentExternalIds(
  partial: Partial<AgentExternalIds> = {},
): AgentExternalIds {
  return { ...EMPTY_AGENT_EXTERNAL_IDS, ...partial }
}

/** Consumo de un agente en un período. Su ausencia (`null`) es "sin medición". */
export const agentConsumoSchema = z.object({
  periodo: periodoSchema,
  trazas: z.number(),
  inputTokens: z.number(),
  outputTokens: z.number(),
  creditosConsumidos: z.number(),
  /** Costo de servir en USD. Lo ven los tres roles: habilita eficiencia. */
  costoUsd: z.number(),
  /** Derivado. null cuando no hubo trazas: dividir por cero no es cero. */
  costoPorTraza: z.number().nullable(),
})
export type AgentConsumo = z.infer<typeof agentConsumoSchema>

export const agentSchema = z.object({
  id: z.string(),
  clientId: z.string(),
  clientNombre: z.string(),
  nombre: z.string(),
  rolFuncional: z.string(),
  estado: agentEstadoSchema,
  /** ISO 8601. null = nunca se registró el despliegue. */
  desplegadoEn: z.string().nullable(),
  /** ISO 8601 de la última traza conocida. null = nunca tuvo actividad. */
  ultimaActividadEn: z.string().nullable(),
  /**
   * Días desde la última actividad, o desde el despliegue si nunca tuvo. Es el
   * "cuántos días lleva así" del bloque de agentes sin uso. null cuando no hay
   * ninguna de las dos fechas: sin fecha no hay antigüedad, y un 0 mentiría.
   */
  diasSinUso: z.number().nullable(),
  /** null = no se pudo medir (ver `langfuseSessionPrefix`). Nunca es "cero". */
  consumo: agentConsumoSchema.nullable(),
  externalIds: agentExternalIdsSchema,
})
export type Agent = z.infer<typeof agentSchema>

/** Fila cruda de consumo, tal como la escribe el adapter. */
export const usageDatapointSchema = z.object({
  periodo: periodoSchema,
  clientId: z.string(),
  /**
   * null = consumo del cliente que no se pudo atribuir a un agente. Pasa cuando
   * la plataforma no estampó el prefijo de sesión; el spec lo declara como el
   * bloqueante real del módulo.
   */
  agentId: z.string().nullable(),
  modelId: z.string(),
  inputTokens: z.number(),
  outputTokens: z.number(),
  trazas: z.number(),
  costoUsd: z.number(),
  creditosConsumidos: z.number(),
  source: usageSourceSchema,
})
export type UsageDatapoint = z.infer<typeof usageDatapointSchema>

/**
 * Modelo del catálogo con su tarifa.
 *
 * Las tarifas **no son un dato de plata restringido**: son el precio de lista del
 * proveedor, el insumo del costo de servir, y sin ellas el costo no es
 * auditable. Lo que sí es restringido es lo que paga el cliente (`UsageEconomia`).
 */
export const modelSchema = z.object({
  id: z.string(),
  nombre: z.string(),
  proveedor: z.string(),
  /** USD por millón de tokens de entrada. null = tarifa sin cargar. */
  precioInputPorMillon: z.number().nullable(),
  precioOutputPorMillon: z.number().nullable(),
  activo: z.boolean(),
})
export type Model = z.infer<typeof modelSchema>

// ---------------------------------------------------------------------------
// Derivados — la forma que devuelven las funciones puras de usage-derived.ts
// ---------------------------------------------------------------------------

/** Un punto de la base de la quema: qué mes y cuánto se quemó. */
export const quemaPuntoSchema = z.object({
  periodo: periodoSchema,
  creditos: z.number(),
})
export type QuemaPunto = z.infer<typeof quemaPuntoSchema>

/** Quema promedio de créditos, con los meses que la componen publicados. */
export const quemaPromedioSchema = z.object({
  promedioMensual: z.number(),
  /** Cuántos meses con dato entraron en el promedio. */
  meses: z.number(),
  /** Los meses exactos y su consumo: la base de la regla 5. */
  base: z.array(quemaPuntoSchema),
})
export type QuemaPromedio = z.infer<typeof quemaPromedioSchema>

/** Fecha proyectada de agotamiento del saldo, con su aritmética publicada. */
export const proyeccionAgotamientoSchema = z.object({
  /** 'YYYY-MM-DD' en UTC. */
  fecha: z.string(),
  dias: z.number(),
  /** Meses de autonomía: saldo / quema mensual. Con decimal, no redondeado. */
  meses: z.number(),
  base: z.object({
    saldo: z.number(),
    quemaMensual: z.number(),
    /** ISO 8601 del instante desde el que se proyecta. */
    desde: z.string(),
  }),
})
export type ProyeccionAgotamiento = z.infer<typeof proyeccionAgotamientoSchema>

/**
 * Variación contra un período de referencia.
 *
 * `pct` puede ser null aun cuando la variación existe: si la base es cero no hay
 * porcentaje que calcular, pero el `delta` absoluto sigue siendo verdadero y se
 * publica igual. Mostrar "+∞%" o "+100%" sería inventar.
 */
export const variacionSchema = z.object({
  actual: z.number(),
  delta: z.number(),
  pct: z.number().nullable(),
  base: z.object({ periodo: periodoSchema, valor: z.number() }),
})
export type Variacion = z.infer<typeof variacionSchema>

/** Participación de una parte sobre un total, con el total publicado. */
export const participacionSchema = z.object({
  pct: z.number(),
  base: z.object({ parte: z.number(), total: z.number() }),
})
export type Participacion = z.infer<typeof participacionSchema>

/**
 * La cadena de activación completa: contratado → instalado → activo.
 *
 * Es la aportación central del módulo. La brecha de activación de Clientes
 * termina en `instalados`; acá se le agrega el tramo que faltaba, y un agente
 * instalado sin una sola traza deja de contarse como éxito.
 */
export const cadenaActivacionSchema = z.object({
  contratados: z.number(),
  instalados: z.number(),
  activos: z.number(),
  /** Contratados que no se instalaron. */
  brechaInstalacion: z.number(),
  /** Instalados que no producen: el bloque "agentes instalados sin uso". */
  brechaUso: z.number(),
  /** instalados / contratados. null si no hay contratados. */
  pctInstalado: z.number().nullable(),
  /** activos / instalados. null si no hay instalados. */
  pctActivoSobreInstalados: z.number().nullable(),
  /** activos / contratados: el tramo completo. null si no hay contratados. */
  pctActivoSobreContratados: z.number().nullable(),
})
export type CadenaActivacion = z.infer<typeof cadenaActivacionSchema>

// ---------------------------------------------------------------------------
// Créditos
// ---------------------------------------------------------------------------

/**
 * Saldo de créditos de una cuenta en un período.
 *
 * `creditosRestantes` **se guarda y no se deriva**, contra la regla general del
 * panel, y el motivo va acá para que nadie lo "arregle": el saldo lo informa la
 * plataforma y arrastra créditos de períodos anteriores, así que no es
 * `comprados − consumidos`. Calcularlo daría un número distinto del que ve el
 * cliente en su cuenta. Comprados y consumidos viajan igual, como base.
 */
export const creditBalanceSchema = z.object({
  clientId: z.string(),
  clientNombre: z.string(),
  periodo: periodoSchema,
  creditosComprados: z.number().nullable(),
  creditosConsumidos: z.number().nullable(),
  creditosRestantes: z.number().nullable(),
  /** Quema promedio de los últimos meses. null = sin meses con dato. */
  quema: quemaPromedioSchema.nullable(),
  /** Fecha proyectada de agotamiento. null = falta saldo o la quema es cero. */
  proyeccion: proyeccionAgotamientoSchema.nullable(),
  /** Id de la suscripción en Stripe. $$: el servidor lo recorta por rol. */
  stripeSubscriptionId: z.string().nullable(),
  provenance: provenanceSchema,
})
export type CreditBalance = z.infer<typeof creditBalanceSchema>

// ---------------------------------------------------------------------------
// Filas de pantalla
// ---------------------------------------------------------------------------

/** Un punto de la serie mensual de consumo. `null` = mes sin medición. */
export const usagePeriodPointSchema = z.object({
  periodo: periodoSchema,
  creditos: z.number().nullable(),
  costoUsd: z.number().nullable(),
  trazas: z.number().nullable(),
  inputTokens: z.number().nullable(),
  outputTokens: z.number().nullable(),
})
export type UsagePeriodPoint = z.infer<typeof usagePeriodPointSchema>

/** Conteo de agentes de una cuenta, en los términos de la cadena. */
export const usageAgentCountsSchema = z.object({
  contratados: z.number(),
  instalados: z.number(),
  activos: z.number(),
  /** Instalados con cero trazas medidas. */
  desplegadosSinUso: z.number(),
  /** Instalados que no se pueden medir por falta de prefijo de sesión. */
  sinMedicion: z.number(),
})
export type UsageAgentCounts = z.infer<typeof usageAgentCountsSchema>

/** Fila de consumo por cuenta. Sin un solo campo de facturación. */
export const usageClientRowSchema = z.object({
  clientId: z.string(),
  clientNombre: z.string(),
  periodo: periodoSchema,
  estado: clientStateSchema,
  /** null = la cuenta no tiene medición en el período. Nunca cero. */
  trazas: z.number().nullable(),
  inputTokens: z.number().nullable(),
  outputTokens: z.number().nullable(),
  creditosConsumidos: z.number().nullable(),
  /** Costo de servir. Los tres roles lo ven. */
  costoUsd: z.number().nullable(),
  /** Derivados; null cuando falta el insumo o el divisor es cero. */
  costoPorAgente: z.number().nullable(),
  costoPorTraza: z.number().nullable(),
  agentes: usageAgentCountsSchema,
  /** Variación de créditos contra el mes anterior, con su base. */
  variacionCreditos: variacionSchema.nullable(),
  variacionCostoUsd: variacionSchema.nullable(),
  /** Últimos meses para el sparkline, del más viejo al más nuevo. */
  serie: z.array(usagePeriodPointSchema),
  /** Del CRM, para el bloque de cuentas en caída. */
  ultimoContacto: z.string().nullable(),
  provenance: provenanceSchema,
})
export type UsageClientRow = z.infer<typeof usageClientRowSchema>

/** Desglose por modelo, con la participación y su base. */
export const modelUsageSchema = z.object({
  model: modelSchema,
  periodo: periodoSchema,
  inputTokens: z.number(),
  outputTokens: z.number(),
  trazas: z.number(),
  costoUsd: z.number(),
  creditosConsumidos: z.number(),
  /** Participación sobre el costo total del período. null si el total es cero. */
  participacionCosto: participacionSchema.nullable(),
  participacionTokens: participacionSchema.nullable(),
  costoPorTraza: z.number().nullable(),
})
export type ModelUsage = z.infer<typeof modelUsageSchema>

/**
 * Lo que paga el cliente y el margen. **Solo admin.**
 *
 * Va en un bloque aparte y no mezclado en `UsageClientRow` justamente para que
 * el corte sea evidente al leer el contrato: la fila de consumo se puede servir
 * a cualquiera, esto no. El costo se repite acá para que el margen sea
 * recalculable sin cruzar dos endpoints.
 */
export const usageEconomiaSchema = z.object({
  clientId: z.string(),
  clientNombre: z.string(),
  periodo: periodoSchema,
  precioUsd: z.number().nullable(),
  costoUsd: z.number().nullable(),
  margenUsd: z.number().nullable(),
  margenPct: z.number().nullable(),
})
export type UsageEconomia = z.infer<typeof usageEconomiaSchema>

/** Tarjeta de la fila héroe, con su base publicada y su proveniencia. */
export const usageKpiSchema = z.object({
  slug: z.string(),
  nombre: z.string(),
  unidad: unitSchema,
  /** null = sin dato. La UI muestra "Sin dato" con la acción de cargarlo. */
  valor: z.number().nullable(),
  /** De qué se compone el número: la regla de números derivables. */
  base: z.array(
    z.object({
      etiqueta: z.string(),
      valor: z.number().nullable(),
      unidad: unitSchema,
    }),
  ),
  /** Variación contra el mes anterior, con su base. */
  variacion: variacionSchema.nullable(),
  provenance: provenanceSchema,
})
export type UsageKpi = z.infer<typeof usageKpiSchema>

// ---------------------------------------------------------------------------
// Payloads
// ---------------------------------------------------------------------------

/** Cuentas sanas: van colapsadas, así que basta el conteo y sus ids. */
export const usageSanasSchema = z.object({
  cantidad: z.number(),
  clientIds: z.array(z.string()),
})
export type UsageSanas = z.infer<typeof usageSanasSchema>

const usageBaseShape = {
  periodo: periodoSchema,
  /** Fila héroe: créditos, agentes activos, costo de servir, cuentas en caída. */
  hero: z.array(usageKpiSchema),
  /** contratado → instalado → activo, del portfolio completo. */
  cadena: cadenaActivacionSchema.nullable(),
  // --- Zona 1: lo que está roto ---
  agentesSinUso: z.array(agentSchema),
  /**
   * Instalados que **no se pueden medir**. Están separados de `agentesSinUso`
   * porque decir "cero trazas" de un agente que nadie midió sería afirmar algo
   * que los datos no sostienen.
   */
  agentesSinMedicion: z.array(agentSchema),
  cuentasEnCaida: z.array(usageClientRowSchema),
  cuentasPorAgotarCreditos: z.array(creditBalanceSchema),
  // --- Zona 2: lo que se mueve ---
  consumoMensual: z.array(usagePeriodPointSchema),
  porCliente: z.array(usageClientRowSchema),
  porModelo: z.array(modelUsageSchema),
  senalesDeExpansion: z.array(usageClientRowSchema),
  // --- Zona 3: lo sano, colapsado ---
  cuentasSanas: usageSanasSchema,
  /** Umbral de caída que define `cuentasEnCaida`, en %. Se muestra en pantalla. */
  umbralCaidaPct: z.number(),
  /** Meses de autonomía por debajo de los cuales una cuenta está por agotar. */
  umbralAgotamientoMeses: z.number(),
  links: linksSchema,
}

const usageBaseSchema = z.object(usageBaseShape)

/** Admin: todo lo anterior más precio y margen por cuenta. */
export const usageAdminPayloadSchema = usageBaseSchema.extend({
  rol: z.literal('admin'),
  economiaPorCuenta: z.array(usageEconomiaSchema),
})

/** Ops: consumo, agentes, modelos, créditos y costo. Sin precio ni margen. */
export const usageOpsPayloadSchema = usageBaseSchema.extend({
  rol: z.literal('ops'),
})

/** Comercial: exactamente lo mismo que ops. */
export const usageComercialPayloadSchema = usageBaseSchema.extend({
  rol: z.literal('comercial'),
})

export const usagePayloadSchema = z.discriminatedUnion('rol', [
  usageAdminPayloadSchema,
  usageOpsPayloadSchema,
  usageComercialPayloadSchema,
])
export type UsagePayload = z.infer<typeof usagePayloadSchema>
export type UsageAdminPayload = z.infer<typeof usageAdminPayloadSchema>
export type UsageOpsPayload = z.infer<typeof usageOpsPayloadSchema>
export type UsageComercialPayload = z.infer<typeof usageComercialPayloadSchema>

export const usageAgentsPayloadSchema = z.object({
  periodo: periodoSchema,
  agentes: z.array(agentSchema),
  cadena: cadenaActivacionSchema.nullable(),
  sinUso: z.array(agentSchema),
  sinMedicion: z.array(agentSchema),
  links: linksSchema,
})
export type UsageAgentsPayload = z.infer<typeof usageAgentsPayloadSchema>

export const usageModelsPayloadSchema = z.object({
  periodo: periodoSchema,
  modelos: z.array(modelUsageSchema),
  /** Catálogo completo, incluidos los modelos sin consumo en el período. */
  catalogo: z.array(modelSchema),
  total: z.object({
    costoUsd: z.number(),
    inputTokens: z.number(),
    outputTokens: z.number(),
    trazas: z.number(),
    creditosConsumidos: z.number(),
  }),
  links: linksSchema,
})
export type UsageModelsPayload = z.infer<typeof usageModelsPayloadSchema>

export const usageCreditsPayloadSchema = z.object({
  periodo: periodoSchema,
  cuentas: z.array(creditBalanceSchema),
  porAgotar: z.array(creditBalanceSchema),
  /** Cuentas del portfolio sin ninguna fila de saldo: estado vacío, no cero. */
  sinDato: z.array(z.object({ clientId: z.string(), clientNombre: z.string() })),
  totales: z.object({
    comprados: z.number().nullable(),
    consumidos: z.number().nullable(),
    restantes: z.number().nullable(),
    /** Cuántas cuentas entraron en los totales: su base. */
    cuentas: z.number(),
  }),
  umbralAgotamientoMeses: z.number(),
  links: linksSchema,
})
export type UsageCreditsPayload = z.infer<typeof usageCreditsPayloadSchema>

// ---------------------------------------------------------------------------
// Constantes de producto
// ---------------------------------------------------------------------------

/**
 * Una caída de −30% o peor contra el mes anterior en una cuenta sana es la
 * alerta de churn más temprana que tiene la empresa (spec §2). El número es del
 * spec, no una elección de implementación, y viaja en el payload para que la
 * pantalla pueda decirlo en vez de dejarlo implícito en el título del bloque.
 */
export const UMBRAL_CAIDA_PCT = -30

/** Por debajo de dos meses de autonomía la conversación comercial ya llega tarde. */
export const UMBRAL_AGOTAMIENTO_MESES = 2

/** Cuántos meses entran en la quema promedio. El spec pide los últimos 3. */
export const MESES_QUEMA_PROMEDIO = 3

/** Meses de sparkline en las tablas de consumo. */
export const MESES_SERIE_CONSUMO = 6

/** Fuente por defecto de una fila de consumo, para el badge de proveniencia. */
export const USAGE_SOURCE_KIND: Record<UsageSource, z.infer<typeof sourceKindSchema>> =
  {
    langfuse: 'langfuse',
    plataforma: 'plataforma',
  }
